'use client';

import { Button, Spinner, Text } from '@lmn/design-system';
import styled, { css } from 'styled-components';
import { useTranslation } from '@/app/components/translations/Translation';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { formatNumber } from '@/utils/numberUtils.ts';
import customCss from '@/app/components/hotel-detail/css/customCss';
import {
  getTripadvisorOtherLanguageReviews,
  isSplitByLanguage,
  needsTripadvisorOtherLanguages,
} from '@/app/common/reviews/tripadvisorReviewsLanguages.ts';
import { useFetchHotelStatic } from '../hooks/useFetchHotelStatic';
import {
  useFetchTripAdvisorPrimaryReviews,
  useFetchTripAdvisorReviews,
  TA_REVIEWS_PER_PAGE,
} from '../hooks/useFetchTripAdvisorReviews';
import { useIsMobile } from '../hooks/useMediaSSR';
import TripAdvisorHotelPageLink from './TripAdvisorHotelPageLink';
import TripAdvisorReviewCard from './TripAdvisorReviewCard';
import TripAdvisorBubbles from './TripAdvisorBubbles';
import TripAdvisorOwl, { TripAdvisorAttribution } from './TripAdvisorOwl';
import TripAdvisorScore from './TripAdvisorScore';
import {
  getTARatingInterval,
  getTripAdvisorHotelReviewsUrl,
  TA_CATEGORY_COLUMN_GAP_PX,
  TA_CATEGORY_DIVIDER_PX,
  TA_CATEGORY_PADDING_PX,
  toTADisplayScore,
} from './tripadvisor.helper';

// Inline (Evo details modal) rendering already gets side padding from the surrounding section.
const Container = styled.div<{ $inline: boolean; $alignWithCategories: boolean }>`
  padding: ${({ $inline }) => ($inline ? '0' : '16px')};

  @media ${customCss.breakpoints.sm} {
    padding: ${({ $inline }) => ($inline ? '0' : '32px')};
  }

  /* MPH-1205: on desktop in the Evo details modal the review text starts on the left edge of the
     summary's category bars. The whole content becomes one two-column grid: column 1 is as wide as
     the score block (or the cards' 200px user column, if wider) and column 2 holds the categories.
     The summary, the language sections, the review lists and the cards are subgrids of it, so the
     cards' content column starts where the category column does, in every locale and for every
     review count. Everything else spans both columns. Without subgrid support the content keeps its
     block layout and today's card alignment. */
  ${({ $alignWithCategories }) =>
    $alignWithCategories &&
    css`
      @supports (grid-template-columns: subgrid) {
        @media ${customCss.breakpoints.lg} {
          display: grid;
          grid-template-columns: auto minmax(0, 1fr);

          > *,
          ${LanguageSections} > *,
          ${LanguageSection} > * {
            grid-column: 1 / -1;
          }

          ${SummaryRow}, ${LanguageSections}, ${LanguageSection}, ${ReviewList} {
            display: grid;
            grid-template-columns: subgrid;
          }

          ${SummaryRow} {
            gap: 0;
          }

          ${ScoreBlock} {
            grid-column: 1;
          }

          /* The summary row's flex gap becomes a margin, so the divider and the category names stay
             exactly where the flex row puts them. */
          ${CategoryGrid} {
            grid-column: 2;
            margin-left: ${TA_CATEGORY_COLUMN_GAP_PX}px;
          }
        }
      }
    `}
`;

const HeaderRow = styled.div<{ $inline: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  /* Inline in the Evo details modal, mobile and tablet follow the tab's space150 rhythm (MPH-839)
     and only desktop takes space300 — there is no tablet step, so the switch is at lg. The
     standalone reviews modal keeps space300 at every width. */
  margin-bottom: ${({ theme, $inline }) => ($inline ? theme.kallisto.space.space150 : theme.kallisto.space.space300)};

  @media ${customCss.breakpoints.lg} {
    margin-bottom: ${({ theme }) => theme.kallisto.space.space300};
  }
`;

/* Inline in the Evo details modal the heading has to step up with the tab's other section titles,
   which go Title3 → Title2 at xl (see Title in HotelPageContentSection and EvoSectionHeading in
   EvoCommon) — otherwise it renders a size larger than them from sm to xl. The standalone reviews
   modal has no such siblings and keeps its own sm step. (MPH-839) */
const GuestReviewsTitle = styled.h2<{ $inline: boolean }>`
  margin: 0;
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  ${({ theme }) => theme.kallisto.mixins.font('Title3')};

  @media ${customCss.breakpoints.sm} {
    ${({ theme, $inline }) => !$inline && theme.kallisto.mixins.font('Title2')};
  }

  @media ${customCss.breakpoints.xl} {
    ${({ theme }) => theme.kallisto.mixins.font('Title2')};
  }
`;

/* Score beside the categories from sm in the standalone modal, but only from lg when inline in the
   Evo details modal (MPH-839): that column is narrower, so tablet keeps the mobile stack. */
const SummaryRow = styled.div<{ $inline?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 20px;

  ${({ $inline }) =>
    !$inline &&
    css`
      @media ${customCss.breakpoints.sm} {
        flex-direction: row;
        align-items: flex-start;
        gap: ${TA_CATEGORY_COLUMN_GAP_PX}px;
      }
    `}

  @media ${customCss.breakpoints.lg} {
    flex-direction: row;
    align-items: flex-start;
    gap: ${TA_CATEGORY_COLUMN_GAP_PX}px;
  }
`;

const ScoreBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const ScoreInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ScoreName = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  ${({ theme }) => theme.kallisto.mixins.font('Title4')};
`;

const BubblesRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
`;

const ReviewCount = styled.span`
  ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
`;

const CategoryGrid = styled.div<{ $inline?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 16px;
  flex: 1;

  // The divider lives on this column (not the score block) so it spans the full row height even
  // though the score block is shorter and top-aligned. It only makes sense once SummaryRow is a
  // row, so it follows the same gate: sm standalone, lg when inline in the modal.
  ${({ $inline }) =>
    !$inline &&
    css`
      @media ${customCss.breakpoints.sm} {
        gap: 16px 32px;
        padding-left: ${TA_CATEGORY_PADDING_PX}px;
        border-left: ${TA_CATEGORY_DIVIDER_PX}px solid ${({ theme }) => theme.commons.colors.gray100};
      }
    `}

  @media ${customCss.breakpoints.lg} {
    gap: 16px 32px;
    padding-left: ${TA_CATEGORY_PADDING_PX}px;
    border-left: ${TA_CATEGORY_DIVIDER_PX}px solid ${({ theme }) => theme.commons.colors.gray100};
  }
`;

const Category = styled.div`
  display: flex;
  flex-direction: column;
`;

const CategoryBarRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.kallisto.space.space150};
`;

const CategoryProgressBar = styled.div<{ $score: number }>`
  background-color: ${({ theme }) => theme.commons.colors.gray100};
  border-radius: 4px;
  height: 8px;
  position: relative;
  flex: 1;

  &::after {
    background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundSuccessContrast};
    border-radius: 4px;
    content: '';
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: ${({ $score }) => $score}%;
  }
`;

const ReviewList = styled.div``;

/* MPH-1202: with the reviews split by language, each list sits under its own Title3 heading and the
   sections are set off by a divider. Every gap in there — divider, heading, card, card divider — is
   one step: 24px on mobile, 32px on desktop, which starts where the summary turns into a row (sm
   standalone, lg inline). The page-language-only list keeps its existing card spacing. */
const LanguageSections = styled.div<{ $inline: boolean }>`
  --ta-language-gap: ${({ theme }) => theme.kallisto.space.space300};
  /* The summary keeps its own 20px padding-bottom and the hotel page link its 16px padding-top, so
     these margins only top them up: 24px after the summary and before the link on mobile, 36px and
     32px on desktop. */
  margin: 4px 0 8px;

  ${({ $inline }) =>
    !$inline &&
    css`
      @media ${customCss.breakpoints.sm} {
        --ta-language-gap: ${({ theme }) => theme.kallisto.space.space400};
        margin: 16px 0;
      }
    `}

  @media ${customCss.breakpoints.lg} {
    --ta-language-gap: ${({ theme }) => theme.kallisto.space.space400};
    margin: 16px 0;
  }
`;

const LanguageSection = styled.section`
  padding-top: var(--ta-language-gap);
  border-top: 1px solid ${({ theme }) => theme.commons.colors.gray100};

  ${ReviewList} > * {
    padding: var(--ta-language-gap) 0;
  }

  ${ReviewList} > :first-child {
    border-top: none;
    padding-top: 0;
  }

  ${ReviewList} > :last-child {
    padding-bottom: 0;
  }

  & + & {
    margin-top: var(--ta-language-gap);
  }
`;

const LanguageSectionTitle = styled.h3`
  margin: 0 0 var(--ta-language-gap);
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  ${({ theme }) => theme.kallisto.mixins.font('Title3')};
`;

const NoReviews = styled.p`
  margin: 0;
  padding: ${({ theme }) => theme.kallisto.space.space200};
  border-radius: ${({ theme }) => theme.kallisto.border.radius.radius100};
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer01};
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  text-align: center;
  ${({ theme }) => theme.kallisto.mixins.font('Body')};
`;

const LoadMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 0;
`;

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px;
`;

type TripAdvisorReviewsModalContentProps = {
  /** Rendered inline in the Evo details modal — the surrounding section already provides padding. */
  inline?: boolean;
};

export default function TripAdvisorReviewsModalContent({ inline = false }: TripAdvisorReviewsModalContentProps) {
  const { t } = useTranslation();
  const { localization, commonData } = usePageWrapperData();
  const { locale } = localization;
  const isMobile = useIsMobile(commonData.isMobileDevice);
  const { data: staticData } = useFetchHotelStatic();
  const { data, isLoading, error, size, setSize } = useFetchTripAdvisorReviews();

  const hotelOpinion = staticData?.hotelOpinion;
  const rawScore = hotelOpinion?.rating ?? 0;
  const ratingInterval = rawScore ? getTARatingInterval(rawScore) : undefined;
  const categories = hotelOpinion?.categories ?? [];
  // MPH-1205: only the Evo modal's desktop summary has category bars for the cards to line up with.
  const alignWithCategories = inline && rawScore > 0 && categories.length > 0;

  const allReviews = data?.flatMap((page) => page.data.reviews ?? []).filter(Boolean) ?? [];
  // MPH-1202: the page-locale reviews (originals and TA translations) always come first. In the split
  // modes, when they are fewer than TA_REVIEWS_TOTAL, a second request for the untranslated reviews
  // fills the rest — only once the first has answered, so a hotel already full in the page language
  // costs no extra call. The page-language section shows as soon as it has reviews; only an empty one
  // waits for the fill, to tell "none in your language" from "none at all".
  const { tripadvisorReviewsLanguages } = localization.configValuesApp;
  const splitByLanguage = isSplitByLanguage(tripadvisorReviewsLanguages);
  const needsOtherLanguages =
    data !== undefined &&
    needsTripadvisorOtherLanguages({
      pageLanguageReviewCount: allReviews.length,
      mode: tripadvisorReviewsLanguages,
      locale,
    });
  const {
    data: primaryData,
    isLoading: isLoadingPrimary,
    error: primaryError,
  } = useFetchTripAdvisorPrimaryReviews(needsOtherLanguages);
  const isLoadingOtherLanguages = needsOtherLanguages && isLoadingPrimary;
  const otherLanguages = getTripadvisorOtherLanguageReviews({
    pageLanguageReviews: allReviews,
    primaryReviews: primaryData?.data.reviews ?? [],
    mode: tripadvisorReviewsLanguages,
    locale,
  });
  const isLoadingReviews = (isLoading && !data) || (isLoadingOtherLanguages && allReviews.length === 0);
  const hasReviews = allReviews.length + otherLanguages.length > 0;
  const noReviewsCopy = t('vacation.select.details.guest.reviews.noresult.title') || 'No reviews found';
  // Live only where "load more" appends to the list; the split sections render whole.
  const renderReviews = (reviews: TripadvisorReview[], live = false) => (
    <ReviewList aria-live={live ? 'polite' : undefined}>
      {reviews.map((review) => (
        <TripAdvisorReviewCard
          key={review.id}
          review={review}
          locale={locale}
          inline={inline}
          alignWithCategories={alignWithCategories}
        />
      ))}
    </ReviewList>
  );
  const reviewCount = hotelOpinion?.numberOfReviews ?? data?.[0]?.data.pagination?.total_elements ?? 0;
  const lastPage = data?.[data.length - 1];
  const totalPages = lastPage?.data.pagination?.total_pages;
  const hasMore =
    totalPages !== undefined ? size < totalPages : (lastPage?.data.reviews?.length ?? 0) >= TA_REVIEWS_PER_PAGE;
  const countFormatted = formatNumber({ locale, number: reviewCount });
  // MPH-763: hotel-reviews resolves the hotel's TripAdvisor page even for a locale with no reviews
  // (it falls back to TA's locations endpoint), so the link does not depend on the list having items.
  const hotelReviewsUrl = getTripAdvisorHotelReviewsUrl(data);

  return (
    <Container $inline={inline} $alignWithCategories={alignWithCategories}>
      <HeaderRow $inline={inline}>
        <GuestReviewsTitle $inline={inline}>
          {t('vacation.select.details.guest.ratings_reviews.title')}
        </GuestReviewsTitle>
        <TripAdvisorAttribution />
      </HeaderRow>

      {rawScore > 0 && (
        <SummaryRow $inline={inline}>
          <ScoreBlock>
            <TripAdvisorScore rawScore={rawScore} size="display" />
            <ScoreInfo>
              {ratingInterval && <ScoreName>{t(ratingInterval.translationKey)}</ScoreName>}
              <BubblesRow>
                <TripAdvisorOwl />
                <TripAdvisorBubbles rawScore={rawScore} css="font-size: 14px;" />
                <ReviewCount>({countFormatted})</ReviewCount>
              </BubblesRow>
            </ScoreInfo>
          </ScoreBlock>

          {categories.length > 0 && (
            <CategoryGrid $inline={inline}>
              {categories.map((category) => (
                <Category key={category.name}>
                  <Text variant="Body" weight="bold" color="textPrimary">
                    {category.name}
                  </Text>
                  <CategoryBarRow>
                    <CategoryProgressBar $score={category.score} />
                    <Text
                      variant="BodySmall"
                      color="textPrimary"
                      css="flex-shrink: 0; min-width: 28px; text-align: left;"
                    >
                      {toTADisplayScore(category.score)}
                    </Text>
                  </CategoryBarRow>
                </Category>
              ))}
            </CategoryGrid>
          )}
        </SummaryRow>
      )}

      {isLoadingReviews ? (
        <SpinnerContainer>
          <Spinner size={32} variant="accent" />
        </SpinnerContainer>
      ) : (error || primaryError) && !hasReviews ? (
        <SpinnerContainer>
          <Text variant="Body" color="textSecondary">
            {t('vacation.select.details.reviews.error') || 'Reviews are currently unavailable.'}
          </Text>
        </SpinnerContainer>
      ) : data === undefined ? null : !hasReviews ? (
        <NoReviews>{noReviewsCopy}</NoReviews>
      ) : splitByLanguage ? (
        <LanguageSections $inline={inline}>
          <LanguageSection>
            <LanguageSectionTitle>
              {t('vacation.select.details.guest.reviews.tripadvisor.your_language') ||
                'Latest reviews in your language'}
            </LanguageSectionTitle>
            {allReviews.length > 0 ? renderReviews(allReviews) : <NoReviews>{noReviewsCopy}</NoReviews>}
          </LanguageSection>
          {isLoadingOtherLanguages && (
            <SpinnerContainer>
              <Spinner size={24} variant="accent" />
            </SpinnerContainer>
          )}
          {otherLanguages.length > 0 && (
            <LanguageSection>
              <LanguageSectionTitle>
                {t('vacation.select.details.guest.reviews.tripadvisor.other_languages') ||
                  'Latest reviews in other languages'}
              </LanguageSectionTitle>
              {renderReviews(otherLanguages)}
            </LanguageSection>
          )}
        </LanguageSections>
      ) : (
        renderReviews(allReviews, true)
      )}

      {isLoading && data && (
        <SpinnerContainer>
          <Spinner size={24} variant="accent" />
        </SpinnerContainer>
      )}

      {/* The split modes list a fixed TA_REVIEWS_TOTAL, so they have no next page. */}
      {hasMore && !isLoading && !splitByLanguage && (
        <LoadMoreContainer>
          <Button variant="primary" appearance="outlined" onClick={() => setSize(size + 1)}>
            {t('vacation.select.details.review.viewmore')}
          </Button>
        </LoadMoreContainer>
      )}

      {hotelReviewsUrl && (
        <TripAdvisorHotelPageLink href={hotelReviewsUrl} reviewCountFormatted={countFormatted} isMobile={!!isMobile} />
      )}
    </Container>
  );
}
